/**
 * @module  Experience/Softgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PRODUCT_OFFERINGS_CONSTANTS } from '@core/aem/components/product-offerings/product-offerings.constants';
import { BrowsePlanConfig } from '@core/aem/interfaces/shop/browse-plan.interface';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { LineDeivceDetails } from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { CartLine } from '@support/api/proxies/responses/api-cart-response.interface';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';

@Component({
  selector: 'tmo-no-plan-needed',
  templateUrl: './no-plan-needed.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/html-accordion.component.scss',
    '../../../../../../style-sheets/components/html-blade-links.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
  ],
})
export class NoPlanNeededComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   * to hold component AEM data
   */
  @Input() public aemContent: BrowsePlanConfig;
  /**
   * Holds true if no plan needed is selected
   */
  @Input() public noPlanNeeded: boolean;
  /**
   * Holds focused tile details
   */
  @Input() public focusedTileIdDetails: LineDeivceDetails;
  /**
   * Holds boolean to disable check box
   */
  @Input() public disableCheckbox: boolean;
  /**
   * string to hold the value of selected plan
   */
  @Input() public isPlanAdded: string;
  /**
   * emitter for triggering call to remove plans from cart
   */
  @Output() public isNoPlanSelected: EventEmitter<boolean>;
  /**
   * Holds true if device is selected
   */
  public isDeviceSelected: boolean;
  /**
   * Stores the static modal for the noPlanNeeded modal data
   */
  public noPlan: StaticModal;
  /**
   * boolean to check if no plan is needed
   */
  public isNoPlanNeededCheck: boolean;

  constructor(
    private objectService: ObjectService,
    private cartSupportService: CartSupportService,
    public pdlService: PdlService,
    public activatedRoute: ActivatedRoute
  ) {
    super(pdlService, activatedRoute);
    this.isNoPlanSelected = new EventEmitter<boolean>();
  }

  /**
   * Loads the initial configuration for the component
   * @returns void
   */
  public ngOnInit(): void {}

  /**
   * Method called when checkbox is checked or unchecked
   * @param isNoPlanNeededCheck boolean
   * @returns void
   */
  public onCheckBoxClick(isNoPlanNeededCheck: boolean): void {
    this.isDeviceSelected = !!this.objectService.get(this.focusedTileIdDetails, 'deviceDetails');
    if (isNoPlanNeededCheck && this.isPlanAdded && this.checkUskuDeviceInLine(this.isPlanAdded)) {
      this.isNoPlanNeededCheck = isNoPlanNeededCheck;
      this.noPlanNeededModal();
    } else {
      this.isNoPlanSelected.emit(isNoPlanNeededCheck);
    }
  }

  /**
   * method to check option selected on modal
   * @param event MouseEvent
   * @returns void
   */
  public planCheck(event: MouseEvent): void {
    if (
      event[PRODUCT_OFFERINGS_CONSTANTS.ID].toLowerCase() !== PRODUCT_OFFERINGS_CONSTANTS.CANCEL
    ) {
      this.isNoPlanSelected.emit(this.isNoPlanNeededCheck);
    } else {
      this.uncheckBox();
    }
    this.closeModal(false);
  }

  /**
   * method to close the modal on click of cancel or cross
   * @param event boolean
   * @returns void
   */
  public closeModal(event: boolean): void {
    if (event) {
      this.uncheckBox();
    }
    this.isNoPlanNeededCheck = false;
    this.noPlan.toggleModalEmitter.emit({ open: false, close: true });
  }

  /**
   * method to uncheck box if rate plan is not removed
   * @returns void
   */
  public uncheckBox(): void {
    const targetElement = document.getElementById('noPlanNeeded') as HTMLInputElement;
    targetElement.checked = false;
  }

  /**
   * Method to destroy component data.
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * method to check if plan is needed or not
   * @returns void
   */
  private noPlanNeededModal(): void {
    const content = {
      backButton: this.aemContent.cancel,
      continue: this.aemContent.continue,
      areYouSure: this.aemContent.areYouSure,
      doYouWishToContinue: this.aemContent.ratePlanRemovalAlert,
    };
    this.noPlan = this.cartSupportService.clearCartModalObject(content, true);
    this.noPlan.toggleModalEmitter.emit({ open: true, close: false });
    // const isWebChannel = SETTINGS.CHANNEL.toLowerCase() ===  CHANNELS.WEB;
    // if (isWebChannel) {
    this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'modalLoad');
    // }
  }

  private checkUskuDeviceInLine(lineId: string): boolean {
    const activeCart = this.cartSupportService.getStatestoreCartReference();
    const activeLineDetails = activeCart.lines.filter((lineItem: CartLine) => {
      return lineItem.id === lineId;
    });
    if (activeLineDetails && activeLineDetails.length > 0) {
      const lineDevice = this.objectService.get(activeLineDetails, '0.device');
      return lineDevice && lineDevice.uSkuIndicator;
    } else {
      return false;
    }
  }
}
